import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { RuntimeConfigService } from './runtime-config.service';
import {
    AdminCustomerConfig,
    AdminTenantConfig
} from '../models/admin-runtime-config.model';

@Injectable({
    providedIn: 'root'
})
export class TenantConfigService {
    private readonly runtimeConfigService = inject(RuntimeConfigService);

    private readonly activeCustomerStorageKey = 'admin_active_customer';

    private activeCustomerKey = '';

    hasTenantSelection(): boolean {
        return this.getTenants().length > 0;
    }

    setActiveCustomerByToken(token: string): void {
        const flavour = this.extractFlavourFromToken(token);

        if (!flavour) {
            console.error('TenantConfigService: no Flavour claim found in token.');
            return;
        }

        const customer = this.runtimeConfigService.getCustomerByFlavour(flavour);

        if (!customer) {
            console.error(`TenantConfigService: no customer found for Flavour "${flavour}".`);
            return;
        }

        this.activeCustomerKey = customer.key;

        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(this.activeCustomerStorageKey, customer.key);
        }
    }

    getActiveCustomer(): AdminCustomerConfig | undefined {
        this.ensureActiveCustomerInitialized();

        if (!this.activeCustomerKey) {
            return undefined;
        }

        return this.runtimeConfigService.getCustomerByKey(this.activeCustomerKey);
    }

    getActiveCustomerKey(): string {
        this.ensureActiveCustomerInitialized();
        return this.activeCustomerKey;
    }

    getTenants(): AdminTenantConfig[] {
        const customer = this.getActiveCustomer();
        return customer?.tenants ?? [];
    }

    getDefaultTenantKey(): string {
        const customer = this.getActiveCustomer();

        if (!customer) {
            return '';
        }

        const defaultTenant = customer.tenants.find(
            tenant => tenant.key === customer.defaultTenantKey && !tenant.disabled
        );

        if (defaultTenant) {
            return defaultTenant.key;
        }

        const firstEnabledTenant = customer.tenants.find(tenant => !tenant.disabled);
        return firstEnabledTenant?.key ?? '';
    }

    getCurrentTenantKey(): string {
        this.ensureActiveCustomerInitialized();

        const customer = this.getActiveCustomer();

        if (!customer) {
            return '';
        }

        const storageKey = this.getTenantStorageKey(customer.key);
        const savedTenantKey =
            typeof window !== 'undefined' && window.localStorage
                ? localStorage.getItem(storageKey)
                : null;

        if (savedTenantKey) {
            const savedTenant = customer.tenants.find(
                tenant => tenant.key === savedTenantKey && !tenant.disabled
            );

            if (savedTenant) {
                return savedTenant.key;
            }
        }

        return this.getDefaultTenantKey();
    }

    setCurrentTenant(key: string): void {
        const customer = this.getActiveCustomer();

        if (!customer) {
            return;
        }

        const targetTenant = customer.tenants.find(
            tenant => tenant.key === key && !tenant.disabled
        );

        if (!targetTenant) {
            return;
        }

        const storageKey = this.getTenantStorageKey(customer.key);

        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(storageKey, targetTenant.key);
        }
    }

    getCurrentTenant(): AdminTenantConfig | undefined {
        const customer = this.getActiveCustomer();

        if (!customer) {
            return undefined;
        }

        const currentTenantKey = this.getCurrentTenantKey();

        return customer.tenants.find(
            tenant => tenant.key === currentTenantKey && !tenant.disabled
        );
    }

    getAdminBaseUrl(): string {
        const currentTenant = this.getCurrentTenant();

        if (!currentTenant) {
            return `${environment.apiUrl}/admin`;
        }

        return `${currentTenant.apiUrl}/admin`;
    }

    /**
     * Forces the currently active customer back to its default tenant.
     * Use this right after login so every new login starts from the default tenant.
     */
    resetCurrentTenantToDefault(): void {
        const customer = this.getActiveCustomer();

        if (!customer) {
            return;
        }

        const defaultTenantKey = this.getDefaultTenantKey();

        if (!defaultTenantKey) {
            return;
        }

        const storageKey = this.getTenantStorageKey(customer.key);

        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.setItem(storageKey, defaultTenantKey);
        }
    }

    clearCustomerContext(): void {
        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.removeItem(this.activeCustomerStorageKey);
        }

        this.activeCustomerKey = '';
    }

    private ensureActiveCustomerInitialized(): void {
        if (this.activeCustomerKey) {
            return;
        }

        const token =
            typeof window !== 'undefined' && window.localStorage
                ? localStorage.getItem('token') || localStorage.getItem('lastValidAccessToken') || ''
                : '';

        if (token) {
            this.setActiveCustomerByToken(token);

            if (this.activeCustomerKey) {
                return;
            }
        }

        const savedCustomerKey =
            typeof window !== 'undefined' && window.localStorage
                ? localStorage.getItem(this.activeCustomerStorageKey)
                : null;

        if (savedCustomerKey) {
            const savedCustomer = this.runtimeConfigService.getCustomerByKey(savedCustomerKey);

            if (savedCustomer) {
                this.activeCustomerKey = savedCustomer.key;
            }
        }
    }

    private getTenantStorageKey(customerKey: string): string {
        return `admin_selected_tenant_${customerKey}`;
    }

    private extractFlavourFromToken(token: string): string | null {
        try {
            const parts = token.split('.');

            if (parts.length !== 3) {
                return null;
            }

            const payload = JSON.parse(this.base64UrlDecode(parts[1]));
            return payload?.Flavour ? String(payload.Flavour) : null;
        } catch (error) {
            console.error('TenantConfigService: failed to decode JWT payload.', error);
            return null;
        }
    }

    private base64UrlDecode(value: string): string {
        const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

        if (typeof window !== 'undefined') {
            return decodeURIComponent(
                Array.prototype.map
                    .call(window.atob(padded), (char: string) =>
                        `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`
                    )
                    .join('')
            );
        }

        return '';
    }
}